// /admin/cookbook-orders: "Request my book" requests waiting for the team, then printed
// cookbook orders, the ones that need attention first.
import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import {
  getCookbookOrders,
  getCookbookRequests,
  refundCookbookOrder,
  retryCookbookOrder,
  updateCookbookRequest,
} from '../../../api/adminV2'
import { useCounts } from '../AdminLayout'
import { firstName, formatDate, LoadError, Loading, money, useFeedback, useLoad } from '../ui'

const STATUS = {
  pending_payment: ['plain', 'Waiting for payment'],
  paid: ['warn', 'Paid'],
  submitted: ['warn', 'Sent to Lulu'],
  in_production: ['warn', 'Printing'],
  shipped: ['good', 'Shipped'],
  delivered: ['good', 'Delivered'],
  failed: ['bad', 'Needs a look'],
  refunded: ['plain', 'Refunded'],
  cancelled: ['plain', 'Cancelled'],
}

const statusDetail = (o) => {
  if (o.attention_reason) return o.attention_reason
  if (o.status === 'shipped' || o.status === 'delivered') {
    return `${o.status === 'delivered' ? 'Delivered' : 'Shipped'}${
      o.shipped_at ? ` ${formatDate(o.shipped_at)}` : ''
    }${o.tracking_url ? ' · tracking available' : ''}`
  }
  if (o.status === 'paid') return 'Goes to Lulu 1 hour after payment'
  if (o.printer_status) return `Lulu: ${o.printer_status.toLowerCase().replace(/_/g, ' ')}`
  if (o.status === 'refunded' && o.refunded_at) return `Refunded ${formatDate(o.refunded_at)}`
  return ''
}

// Low-quality photos the customer chose to print anyway: what we showed them and when they
// answered. Proof if a printed photo disappoints.
const PhotoApprovals = ({ list }) =>
  list?.length ? (
    <details style={{ marginTop: 4, fontSize: 12.5 }}>
      <summary className="a-pill a-warn" style={{ cursor: 'pointer' }}>
        Kept {list.length} low-quality photo{list.length === 1 ? '' : 's'}
      </summary>
      {list.map((p, i) => (
        <div key={p.approval_id || i} className="a-muted" style={{ marginTop: 4 }}>
          “{p.message}” Answered “{p.answer}”
          {p.answered_at ? ` on ${formatDate(p.answered_at)}` : ''}
          {p.approval_id ? ` (record #${p.approval_id})` : ''}.
        </div>
      ))}
    </details>
  ) : null

const REQUEST_STATUS = [
  ['new', 'New: email them'],
  ['contacted', 'Contacted'],
  ['done', 'Done'],
  ['cancelled', 'Cancelled'],
]
const BLOCK = { display: 'block' }
const REQUEST_TONE = { new: 'bad', contacted: 'warn', done: 'good', cancelled: 'plain' }

// People who asked for their book without paying online. The team emails them to finish it.
const CookbookRequests = () => {
  const { toast, toastError } = useFeedback()
  const { refreshCounts } = useCounts()
  const [params] = useSearchParams()
  const focusId = Number(params.get('request')) || null
  const { data, error, loading, reload, setData } = useLoad(getCookbookRequests, [])
  const [showClosed, setShowClosed] = useState(false)
  const [busy, setBusy] = useState(null)
  const focusRef = useRef(null)

  useEffect(() => {
    if (data && focusRef.current) focusRef.current.scrollIntoView({ block: 'center' })
  }, [data, focusId])

  if (error && !data) return <LoadError error={error} onRetry={reload} />
  if (loading && !data) return null

  const all = data || []
  const open = all.filter((r) => r.open)
  const shown = all.filter((r) => showClosed || r.open || r.id === focusId)
  const waiting = all.filter((r) => r.status === 'new').length
  const who = (r) => r.user?.fullname || r.user?.email || 'Unknown customer'

  const save = (r, changes) => {
    setBusy(r.id)
    return updateCookbookRequest(r.id, changes)
      .then((updated) => {
        setData(all.map((x) => (x.id === r.id ? { ...x, ...updated } : x)))
        refreshCounts()
        return updated
      })
      .catch((e) => {
        toastError(e)
        throw e
      })
      .finally(() => setBusy(null))
  }

  const setStatus = (r, status) =>
    save(r, { status })
      .then(() => toast(`Request #${r.id}: ${status}`))
      .catch(() => null)

  const saveNote = (r, notes) => {
    if ((notes || '') === (r.notes || '')) return
    save(r, { notes: notes || null })
      .then(() => toast(`Note saved on request #${r.id}`))
      .catch(() => null)
  }

  if (!all.length) return null

  return (
    <>
      <div className="a-head">
        <div>
          <h2 style={{ margin: 0 }}>Requested books</h2>
          <p className="a-sub">
            People who asked for their cookbook while online checkout is coming soon. Email each one
            within 1 business day.
            {waiting ? ` ${waiting} new.` : ''}
          </p>
        </div>
        {all.length > open.length ? (
          <button type="button" className="a-btn" onClick={() => setShowClosed((v) => !v)}>
            {showClosed
              ? 'Hide done and cancelled'
              : `Show ${all.length - open.length} done or cancelled`}
          </button>
        ) : null}
      </div>
      <p className="a-notice" style={{ margin: '0 0 12px' }}>
        To let someone pay themselves, tick “Can order cookbooks online (beta)” on their person
        page, then email them to open their cookbook and continue to payment.
      </p>
      <section className="a-panel" style={{ marginBottom: 24 }}>
        {shown.length ? (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Request</th>
                  <th>Book</th>
                  <th>Ship to</th>
                  <th className="a-num-cell">Quoted</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const a = r.shipping_address || {}
                  return (
                    <tr
                      key={r.id}
                      ref={r.id === focusId ? focusRef : null}
                      className={r.id === focusId ? 'a-highlight' : ''}
                    >
                      <td className="a-who">
                        <b>
                          #{r.id} ·{' '}
                          {r.user?.id ? (
                            <Link to={`/admin/people/${r.user.id}`}>{who(r)}</Link>
                          ) : (
                            who(r)
                          )}
                        </b>
                        <span style={BLOCK}>
                          {r.user?.email ? (
                            <a href={`mailto:${r.user.email}`}>{r.user.email}</a>
                          ) : null}
                        </span>
                        <span style={BLOCK}>
                          Asked {formatDate(r.created_at)}
                          {r.updated_at && r.updated_at !== r.created_at
                            ? `, updated ${formatDate(r.updated_at)}`
                            : ''}
                        </span>
                        {r.user?.can_order_online ? (
                          <span className="a-pill a-good" style={{ marginTop: 4 }}>
                            Can order online
                          </span>
                        ) : null}
                      </td>
                      <td className="a-who">
                        <b>{r.title || 'Untitled cookbook'}</b>
                        <span style={BLOCK}>
                          {[
                            r.product_name,
                            r.size_label,
                            r.page_count ? `${r.page_count} pages` : null,
                            `${r.quantity} ${r.quantity === 1 ? 'copy' : 'copies'}`,
                            r.shipping_name,
                            r.is_gift ? 'gift' : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                        <PhotoApprovals list={r.photo_approvals} />
                        {r.cookbook_order_id ? (
                          <span style={BLOCK}>
                            <Link to={`/admin/cookbook-orders?order=${r.cookbook_order_id}`}>
                              Paid as order #{r.cookbook_order_id}
                            </Link>
                          </span>
                        ) : null}
                      </td>
                      <td className="a-who">
                        <b>{a.name}</b>
                        <span style={BLOCK}>
                          {[a.street1, a.street2].filter(Boolean).join(', ')}
                          <br />
                          {a.city}, {a.state_code} {a.postcode}
                          <br />
                          {a.phone_number}
                        </span>
                      </td>
                      <td className="a-num-cell">
                        {money(r.due)}
                        {r.voucher ? (
                          <div className="a-muted" style={{ fontSize: 12 }}>
                            after {money(r.voucher)} voucher
                          </div>
                        ) : null}
                      </td>
                      <td style={{ minWidth: 190 }}>
                        <span className={`a-pill a-${REQUEST_TONE[r.status] || 'plain'}`}>
                          {(REQUEST_STATUS.find(([k]) => k === r.status) || [])[1] || r.status}
                        </span>
                        <select
                          className="a-input"
                          style={{ marginTop: 6 }}
                          value={r.status}
                          disabled={busy === r.id}
                          aria-label={`Status of request #${r.id}`}
                          onChange={(e) => setStatus(r, e.target.value)}
                        >
                          {REQUEST_STATUS.map(([k, label]) => (
                            <option key={k} value={k}>
                              {label}
                            </option>
                          ))}
                        </select>
                        <textarea
                          className="a-input"
                          style={{ marginTop: 6, minHeight: 54, fontSize: 13 }}
                          placeholder="Notes for the team"
                          defaultValue={r.notes || ''}
                          disabled={busy === r.id}
                          aria-label={`Notes on request #${r.id}`}
                          onBlur={(e) => saveNote(r, e.target.value.trim())}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="a-empty">No open requests.</p>
        )}
      </section>
    </>
  )
}

const CookbookOrders = () => {
  const { toast, confirm } = useFeedback()
  const { refreshCounts } = useCounts()
  const [params] = useSearchParams()
  const focusId = Number(params.get('order')) || null
  const { data, error, loading, reload } = useLoad(getCookbookOrders, [])
  const [showAll, setShowAll] = useState(false)
  const focusRef = useRef(null)

  useEffect(() => {
    if (data && focusRef.current) focusRef.current.scrollIntoView({ block: 'center' })
  }, [data, focusId])

  if (error && !data) return <LoadError error={error} onRetry={reload} />
  if (loading && !data) return <Loading />

  const all = data || []
  const unpaid = all.filter((o) => o.status === 'pending_payment' && !o.needs_attention)
  const orders = [...all]
    .filter(
      (o) => showAll || o.status !== 'pending_payment' || o.needs_attention || o.id === focusId
    )
    .sort((a, b) => Number(!!b.needs_attention) - Number(!!a.needs_attention) || b.id - a.id)
  const attention = all.filter((o) => o.needs_attention).length
  const who = (o) => o.user?.fullname || o.user?.email || 'Unknown customer'

  const retry = (o) =>
    confirm({
      title: `Send order #${o.id} to Lulu again?`,
      text: 'Lulu gets the book files again and starts a new print job.',
      action: 'Send to Lulu',
      run: () => retryCookbookOrder(o.id),
    }).then((ok) => {
      if (!ok) return
      toast(`Order #${o.id} sent to Lulu again`)
      refreshCounts()
      reload()
    })

  const refund = (o) =>
    confirm({
      title: `Refund order #${o.id}?`,
      text: `${firstName(o.user?.fullname)} gets ${money(
        o.total
      )} back on their card. Cancel the Lulu job first if it is still open.`,
      action: `Refund ${money(o.total)}`,
      danger: true,
      run: () => refundCookbookOrder(o.id),
    }).then((ok) => {
      if (!ok) return
      toast(`Order #${o.id} refunded`)
      refreshCounts()
      reload()
    })

  return (
    <>
      <div className="a-head">
        <div>
          <h1>Cookbook orders</h1>
          <p className="a-sub">
            Printed through Lulu. Orders that need you are at the top.
            {attention ? ` ${attention} ${attention === 1 ? 'needs' : 'need'} a look.` : ''}
          </p>
        </div>
        {unpaid.length ? (
          <button type="button" className="a-btn" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Hide unpaid checkouts' : `Show ${unpaid.length} unpaid checkouts`}
          </button>
        ) : null}
      </div>
      <CookbookRequests />
      {all.length ? <h2 style={{ margin: '0 0 10px' }}>Paid orders</h2> : null}
      <section className="a-panel">
        {orders.length ? (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Book</th>
                  <th>Status</th>
                  <th className="a-num-cell">Paid</th>
                  <th className="a-num-cell">Lulu cost</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const [tone, label] = o.needs_attention
                    ? ['bad', 'Needs a look']
                    : STATUS[o.status] || ['plain', o.status]
                  const canRetry = ['failed', 'paid'].includes(o.status)
                  const canRefund =
                    o.total !== null &&
                    o.total !== undefined &&
                    !['refunded', 'shipped', 'delivered'].includes(o.status)
                  return (
                    <tr
                      key={o.id}
                      ref={o.id === focusId ? focusRef : null}
                      className={o.id === focusId ? 'a-highlight' : ''}
                    >
                      <td className="a-who">
                        <b>
                          #{o.id} ·{' '}
                          {o.user?.id ? (
                            <Link to={`/admin/people/${o.user.id}`}>{who(o)}</Link>
                          ) : (
                            who(o)
                          )}
                        </b>
                        <span>{formatDate(o.paid_at || o.created_at)}</span>
                      </td>
                      <td className="a-who">
                        <b>{o.title || 'Untitled cookbook'}</b>
                        <span>
                          {[
                            o.product_name,
                            o.size_label,
                            o.page_count ? `${o.page_count} pages` : null,
                            o.quantity > 1 ? `${o.quantity} copies` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                        <PhotoApprovals list={o.photo_approvals} />
                      </td>
                      <td>
                        <span className={`a-pill a-${tone}`}>{label}</span>
                        {statusDetail(o) ? (
                          <div className="a-muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                            {statusDetail(o)}
                          </div>
                        ) : null}
                        {o.printer_job_id ? (
                          <div className="a-muted a-mono" style={{ fontSize: 12, marginTop: 2 }}>
                            Lulu job {o.printer_job_id}
                          </div>
                        ) : null}
                      </td>
                      <td className="a-num-cell">{money(o.total)}</td>
                      <td className="a-num-cell">{money(o.print_cost)}</td>
                      <td className="a-right">
                        <div className="a-btn-row" style={{ justifyContent: 'flex-end' }}>
                          {canRetry ? (
                            <button
                              type="button"
                              className="a-btn a-small a-primary"
                              onClick={() => retry(o)}
                            >
                              Send to Lulu again
                            </button>
                          ) : null}
                          {canRefund ? (
                            <button
                              type="button"
                              className="a-btn a-small"
                              onClick={() => refund(o)}
                            >
                              Refund
                            </button>
                          ) : null}
                          {o.tracking_url ? (
                            <a
                              className="a-btn a-small"
                              href={o.tracking_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Tracking
                            </a>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="a-empty">No cookbook orders yet.</p>
        )}
      </section>
    </>
  )
}

export default CookbookOrders
